import { useEffect, useState } from "react";
import { CircularProgress, LinearProgress } from "@mui/material";
import DetailsPanel from "components/Elements/DetailsPanel";
import DragAndDropTable from "components/Modules/DragAndDropTable";
import DeleteDialog from "components/Elements/DeleteDialog";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import { Apis } from "services/api";
import ActionButtonStyle from "styles/application/ActionButtonStyle";
import AddOrEditAttachments from "./AddOrEditAttachments";
import {
	addModelTaskAttachment,
	patchModelTaskAttachment,
	repositionModelTaskAttachment,
	uploadModelTaskAttachmentDocument,
} from "services/models/modelDetails/modelTaskAttachments";
import withMount from "components/HOC/withMount";
import { pasteModelTaskDocument } from "services/models/modelDetails/modelTasks/pasteApi";
import GeneralButton from "components/Elements/GeneralButton";
import {
	EDIT_ACCESS,
	FULL_ACCESS,
	READONLY_ACCESS,
} from "constants/AccessTypes/AccessTypes";
import useModelTaskAttachments from "hooks/ModuleTaskTableHooks/useModalTaskAttachments";

const AT = ActionButtonStyle();

const Attachments = ({ taskInfo, access, isMounted }) => {
	const [selectedID, setSelectedID] = useState(null);
	const [openAddAttachment, setOpenAddAttachment] = useState(false);
	const [openEditAttachment, setOpenEditAttachment] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [pastePart, setPastePart] = useState(true);
	const [isPasting, setIsPasting] = useState(false);

	// hooks
	const dispatch = useDispatch();

	const {
		attachments,
		loading,
		setAttachments,
		setOriginalAttachments,
		originalAttachments,
		fetchAttachments,
		CtxDispatch,
		state,
		MtxDispatch,
	} = useModelTaskAttachments({ taskInfo, isMounted });

	useEffect(() => {
		setPastePart(state.isAttachmentsTaskDisabled);
	}, [state]);

	// handle dragging of attachment

	const handleDragEnd = async (e) => {
		if (!e.destination) {
			return;
		}

		if (e.destination.index === e.source.index) return;

		const result = [...attachments];
		const [removed] = result.splice(e.source.index, 1);
		result.splice(e.destination.index, 0, removed);
		setAttachments(result);

		try {
			let payloadBody = {
				id: e.draggableId,
				destinationId: e?.destination?.destinationId,
				direction: e?.destination?.direction,
			};
			const response = await repositionModelTaskAttachment(
				e.draggableId,
				payloadBody
			);
			if (response.status) {
				const newDate = result.map((x, i) =>
					i === e.destination.index ? { ...x, pos: response.data.pos } : x
				);
				setOriginalAttachments(newDate);
				setAttachments(newDate);
			} else {
				setAttachments(originalAttachments);
			}
		} catch (error) {
			setAttachments(originalAttachments);
		}
	};

	// show popup for deletion of attachment
	const showDeleteAttachmentPopUp = (TaskPartID) => {
		setOpenDeleteDialog(true);
		setSelectedID(TaskPartID);
	};

	// remove attachment from attachment list
	const handleRemoveData = (id) => {
		const newData = [...attachments].filter(function (item) {
			return item.id !== id;
		});
		setAttachments(newData);
		setOriginalAttachments(newData);
		CtxDispatch({
			type: "TAB_COUNT",
			payload: {
				countTab: "documentCount",
				data: newData?.length,
			},
		});
		if (newData.length > 0) {
			CtxDispatch({
				type: "SET_DOCUMENTS",
				payload: true,
			});
		} else {
			CtxDispatch({
				type: "SET_DOCUMENTS",
				payload: false,
			});
		}
	};

	const createAttachment = async (newPermit) => {
		let response, callApi;
		if (newPermit.file) {
			response = await uploadModelTaskAttachmentDocument({
				Filename: newPermit.file.name,
				ModelVersionTaskId: taskInfo.id,
			});
			if (response.status) {
				try {
					await fetch(response.data.url, {
						method: "PUT",
						body: newPermit.file,
					});
					callApi = true;
				} catch (error) {
					callApi = false;
				}
			} else {
				callApi = false;
			}
		} else {
			callApi = true;
		}

		if (callApi) {
			return await addModelTaskAttachment({
				ModelVersionTaskID: taskInfo.id,
				name: newPermit.name,
				documentKey: response?.data?.key ?? null,
				link: newPermit?.link !== "" ? newPermit.link : null,
				filename: newPermit?.filename || null,
			});
		} else {
			return {
				status: false,
				data: { detail: response?.data?.detail || "File Upload Failed" },
			};
		}
	};

	// show popUp for edit of attachment
	const showEditAttachmentPopUp = (row) => {
		row = attachments.find((r) => r.id === row);
		setOpenEditAttachment(true);
		setSelectedID(row);
	};

	const editAttachment = async (payload) => {
		let response;
		if (payload.file?.name) {
			response = await uploadModelTaskAttachmentDocument({
				Filename: payload.file.name,
				ModelVersionTaskId: taskInfo.id,
			});
			let errMsg = response?.data?.detail;
			if (response.status) {
				try {
					await fetch(response.data.url, {
						method: "PUT",
						body: payload.file,
					});
				} catch (error) {
					response = false;
					return {
						status: false,
						data: { detail: "File Upload Failed" },
					};
				}
			} else {
				response = false;
				return {
					status: false,
					data: { detail: errMsg || "File Upload Failed" },
				};
			}
		} else {
			response = false;
		}
		return await patchModelTaskAttachment(selectedID.id, [
			{
				op: "replace",
				path: "name",
				value: payload?.name,
			},
			{
				op: "replace",
				path: "link",
				value: payload?.link === "" ? null : payload?.link,
			},
			{
				op: "replace",
				path: "documentKey",
				value: response?.data?.key ?? payload?.file,
			},
			{
				op: "replace",
				path: "filename",
				value: payload.filename,
			},
		]);
	};

	const handleCopy = (id) => {
		localStorage.setItem("taskdocument", id);
		MtxDispatch({ type: "DISABLE_ATTACHMENTS_TASK", payload: false });
	};

	const handlePaste = async () => {
		setIsPasting(true);
		try {
			const taskPartId = localStorage.getItem("taskdocument");
			let result = await pasteModelTaskDocument(taskInfo.id, {
				modelVersionTaskDocumentID: taskPartId,
			});

			if (!isMounted.aborted) {
				if (result.status) {
					await fetchAttachments(false);
				} else {
					// errorResponse(result);
					dispatch(showError(result?.data?.detail || "Could not paste"));
				}
			}
		} catch (e) {
			return;
		}
		setIsPasting(false);
	};

	const checkcopyPartStatus = async () => {
		try {
			const taskId = localStorage.getItem("taskdocument");

			if (taskId) {
				setPastePart(false);
			}
		} catch (error) {
			return;
		}
	};

	const visibilitychangeCheck = function () {
		if (!document.hidden) {
			checkcopyPartStatus();
		}
	};

	useEffect(() => {
		checkcopyPartStatus();
		document.addEventListener("visibilitychange", visibilitychangeCheck);
		return () =>
			document.removeEventListener("visibilitychange", visibilitychangeCheck);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (loading) return <CircularProgress />;
	return (
		<div style={{ marginBottom: "20px" }}>
			<AddOrEditAttachments
				open={openAddAttachment}
				title={`Add Attachment`}
				closeHandler={() => {
					setOpenAddAttachment(false);
				}}
				data={null}
				createProcessHandler={createAttachment}
				fetchData={() => fetchAttachments(false)}
			/>
			<AddOrEditAttachments
				open={openEditAttachment}
				title={`Attachment`}
				closeHandler={() => {
					setOpenEditAttachment(false);
				}}
				data={{
					name: selectedID?.name?.props?.children,
					link: selectedID?.link === null ? "" : selectedID?.link,
					file: selectedID?.documentKey,
					filename: selectedID?.filename,
				}}
				createProcessHandler={editAttachment}
				fetchData={() => fetchAttachments(false)}
				isEdit
			/>
			<DeleteDialog
				entityName={`Attachment`}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteEndpoint={Apis.ModelVersionTaskAttachments}
				deleteID={selectedID}
				handleRemoveData={handleRemoveData}
			/>
			<div
				className="detailsContainer topContainerCustomCaptions"
				style={{ alignItems: "center" }}
			>
				<DetailsPanel header={`Attachments`} dataCount={attachments.length} />
				{access === FULL_ACCESS && !state?.modelDetail?.isPublished && (
					<>
						<GeneralButton
							style={{ background: "#ED8738", width: "200px" }}
							onClick={handlePaste}
							disabled={pastePart}
						>
							Paste {"Attachment"}
						</GeneralButton>
						<AT.GeneralButton
							onClick={() => {
								setOpenAddAttachment(true);
							}}
							style={{ width: "200px" }}
						>
							Add Attachment
						</AT.GeneralButton>
					</>
				)}
			</div>
			{isPasting ? <LinearProgress /> : null}

			<DragAndDropTable
				data={attachments}
				headers={["Name", "FileSize"]}
				columns={[
					{ id: 2, name: "name", style: { width: "50vw" } },
					{ id: 3, name: "filesize", width: { width: "50vw" } },
				]}
				handleDragEnd={handleDragEnd}
				menuData={[
					{
						name: "Edit",
						handler: showEditAttachmentPopUp,
						isDelete: false,
					},
					{
						name: "Copy",
						handler: handleCopy,
					},
					{
						name: "Remove",
						handler: showDeleteAttachmentPopUp,
						isDelete: true,
					},
				].filter((x) => {
					if (state?.modelDetail?.isPublished) {
						return x?.name === "Copy";
					}
					if (access === FULL_ACCESS) return true;
					if (access === EDIT_ACCESS) {
						if (x.name === "Edit") return true;
						else return false;
					}
					return false;
				})}
				isModelEditable
				disableDnd={
					access === READONLY_ACCESS || state.modelDetail?.isPublished
				}
			/>
		</div>
	);
};

export default withMount(Attachments);
